/**
 * @file FormFooter.tsx
 *
 * Action footer shared by the routed admin forms (Products, Categories,
 * Coupons, Custom Shipping, Local Pickup). Mirrors WSB-v2's form footer: the
 * actions are left-aligned and flow inline at the end of the form (not pinned),
 * stacking column-reverse on mobile and switching to a row on tablet and up via
 * Gravity breakpoints. Callers pass the action buttons as children and may pass
 * a className to constrain the footer to the form-column width.
 */

import type { ReactNode } from 'react';

interface FormFooterProps {
  children: ReactNode;
  /** Extra class merged onto the footer, e.g. to constrain its width. */
  className?: string;
}

/**
 * Renders the shared, responsive form action footer.
 *
 * @param props.children - The footer's action buttons.
 * @param props.className - Optional extra class merged onto the footer.
 *
 * @returns The footer element.
 */
export function FormFooter({ children, className }: FormFooterProps) {
  const classes = [
    'gv-flex gv-w-full gv-gap-md gv-justify-start gv-flex-wrap gv-flex-col-reverse gv-tab-flex-row',
    className,
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={classes} data-testid="form-footer">
      {children}
    </div>
  );
}
